import {
  booleanAttribute,
  Component,
  EventEmitter,
  inject,
  input,
  type OnInit,
  Output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { Creditor } from '@core/models/creditor';
import { FinoFormsModule, SubmittableForm } from '@fino-ui/forms';
import { FinoIconModule } from '@fino-ui/icon';
import { CaptureTransactionService } from '@modules/capture-transaction/capture-transaction.service';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  Subject,
  switchMap,
} from 'rxjs';
import { CreditorPipe } from '../../../core/pipes/creditor.pipe';
import { TypeaheadComponent } from '../../typeahead/typeahead.component';

@Component({
  selector: 'app-creditor-search',
  imports: [
    TypeaheadComponent,
    FinoIconModule,
    CreditorPipe,
    FormsModule,
    FinoFormsModule,
  ],
  templateUrl: './creditor-search.component.html',
  styleUrl: './creditor-search.component.sass',
})
export class CreditorSearchComponent implements OnInit {
  cidTerm$ = new Subject<string>();
  public creditorLoading = signal(false);
  public creditorResults = signal<any[]>([]);

  private readonly captureTransactionService = inject(
    CaptureTransactionService,
  );

  directDebitSelected = new EventEmitter<boolean>();

  readonly form = input<SubmittableForm>(new SubmittableForm({}));
  readonly controlName = input<string>('');
  readonly label = input<string>(undefined);

  readonly byName = input<boolean, unknown>(undefined, {
    transform: booleanAttribute,
  });

  @Output() selected = new EventEmitter<Creditor>();

  ngOnInit(): void {
    this.setupCidSearch();
  }

  creditorIdSearchChanged(searchTerm: string) {
    this.cidTerm$.next(searchTerm);
  }

  keyboardSelectedCreditor($event: any) {
    this.handleCreditorResultSelected(this.creditorResults()[$event]);
  }

  handleCreditorResultSelected(creditor: Creditor) {
    this.directDebitSelected.next(true);
    this.selected.next(creditor);
  }

  close() {
    this.directDebitSelected.next(true);
  }

  isIgnorableCreditor(creditor: Creditor) {
    return creditor.ignoreCreditor || creditor.preferredContact === 'online';
  }

  setupCidSearch() {
    this.cidTerm$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        filter((term: string) => !!term?.trim()),
        switchMap((term) => {
          this.creditorLoading.set(true);
          return this.captureTransactionService.getCreditors(
            term,
            this.byName() ? 'q' : 'cid',
          );
        }),
      )
      .subscribe({
        next: (results) => {
          this.creditorLoading.set(false);
          this.creditorResults.set(results as []);
        },
        error: () => {
          this.creditorLoading.set(false);
        },
      });
  }
}
